import {
  type Observation,
  type SeriesObservation,
  shownReference,
} from '../observation/observation';
import { isOnPromotion } from './promotion';
import { sumValue, sumVolume } from './volume-value-asp';

/** A share result pairing numerator and denominator from the same measure call (AC-01). */
export type ShareResult = {
  readonly numerator: number;
  readonly denominator: number;
};

/**
 * Volume-based market share: subject units / universe units.
 * Both totals are derived from the same sumVolume path.
 */
export function volumeShare(
  subject: readonly Observation[],
  universe: readonly Observation[]
): ShareResult {
  return {
    numerator: sumVolume(subject),
    denominator: sumVolume(universe),
  };
}

/**
 * Value-based market share: subject revenue / universe revenue.
 * Both totals are derived from the same sumValue path.
 */
export function valueShare(
  subject: readonly Observation[],
  universe: readonly Observation[]
): ShareResult {
  return {
    numerator: sumValue(subject),
    denominator: sumValue(universe),
  };
}

/**
 * On-promotion share: units sold on promotion / total units sold.
 * An observation is promotional when its discount depth > PROMO_THRESHOLD (10 percent).
 * Units-weighted; zero-unit observations are excluded from both totals (AC-06).
 */
export function computePromoShare(obs: readonly SeriesObservation[]): ShareResult {
  let promoUnits = 0;
  let totalUnits = 0;
  for (const o of obs) {
    const units = o.signal.unitsSold;
    if (units === null || units <= 0) continue;
    totalUnits += units;
    if (isOnPromotion(o.observed.price, shownReference(o.observed))) {
      promoUnits += units;
    }
  }
  return { numerator: promoUnits, denominator: totalUnits };
}
